import { useCallback } from 'react'
import { UseToastOptions } from '@chakra-ui/react'
import { validatePhoneNumber } from '../../../../../utils/validatePhone'
import {
  validateDateFormat,
  isDateEmpty,
  isDateBefore,
} from '../../../../../utils/dateValidation'
import { validateAge } from '../../../../../utils/ageValidation'
import { ProfileValidationContext, ValidationResult } from '../types/validation'

/**
 * Custom hook for PersonalTab form validation
 * Extracts all validation logic from the submit function for better separation of concerns
 */
export default function usePersonalTabValidation(
  toast: (options: UseToastOptions) => void
) {
  const validateProfile = useCallback(
    (context: ProfileValidationContext): ValidationResult => {
      const {
        profile,
        isMine,
        extraInfoRequired,
        areDateFieldsDisabled,
        areFirstSymptomsFieldsDisabled,
      } = context

      // 1. Terms agreement check
      if (!profile.agreedToProfileCheckbox && isMine) {
        return {
          isValid: false,
          errorMessage: 'You must agree to the terms and conditions',
        }
      }

      // 2. First name validation
      if (profile.firstName && profile.firstName.trim().length < 2) {
        return {
          isValid: false,
          errorMessage: 'First name too short',
        }
      }

      // 3. Birth date format validation
      if (profile.birthDate && !validateDateFormat(profile.birthDate)) {
        toast({
          title: 'Validation Error',
          description: 'Invalid birth date format. Please use YYYY-MM-DD.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 4. Age validation
      if (profile.birthDate) {
        const ageResult = validateAge(profile.birthDate)
        if (!ageResult.isValid) {
          toast({
            title: 'Validation Error',
            description: ageResult.errorMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          return { isValid: false }
        }
      }

      // 5. Diagnosis date validation (only if not empty/incomplete)
      if (
        profile.diagnosisDate &&
        !isDateEmpty(profile.diagnosisDate) &&
        !validateDateFormat(profile.diagnosisDate)
      ) {
        toast({
          title: 'Validation Error',
          description: 'Invalid diagnosis date format. Please use YYYY-MM-DD.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 6. First symptoms date validation (only if not empty/incomplete)
      if (
        profile.firstSymptomsDate &&
        !isDateEmpty(profile.firstSymptomsDate) &&
        !validateDateFormat(profile.firstSymptomsDate)
      ) {
        toast({
          title: 'Validation Error',
          description:
            'Invalid first symptoms date format. Please use YYYY-MM-DD.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 7. Required diagnosis date check
      if (
        extraInfoRequired &&
        !areDateFieldsDisabled &&
        isDateEmpty(profile.diagnosisDate)
      ) {
        toast({
          title: 'Validation Error',
          description: 'Date of Diagnosis is required for your diagnosis type.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 8. Required first symptoms date check
      if (
        !areFirstSymptomsFieldsDisabled &&
        isDateEmpty(profile.firstSymptomsDate)
      ) {
        toast({
          title: 'Validation Error',
          description:
            'Date of First Symptoms is required for your diagnosis type.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 9. Date order validation (first symptoms must be before diagnosis)
      if (
        !areFirstSymptomsFieldsDisabled &&
        !areDateFieldsDisabled &&
        profile.firstSymptomsDate &&
        profile.diagnosisDate &&
        validateDateFormat(profile.firstSymptomsDate) &&
        validateDateFormat(profile.diagnosisDate) &&
        !isDateBefore(profile.firstSymptomsDate, profile.diagnosisDate)
      ) {
        toast({
          title: 'Validation Error',
          description:
            'Date of First Symptoms must be before Date of Diagnosis.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return { isValid: false }
      }

      // 10. Emergency contact validation
      if (
        extraInfoRequired &&
        (!profile.emergencyContactFirstName ||
          !profile.emergencyContactLastName ||
          !profile.emergencyContactEmail ||
          !profile.emergencyContactPhone)
      ) {
        return {
          isValid: false,
          errorMessage:
            'Emergency contact information is required for your diagnosis type.',
        }
      }

      // 11. Phone number validation
      if (profile.phoneNumber) {
        if (!profile.phoneCountryCode) {
          toast({
            title: 'Validation Error',
            description: 'Please select a country code for your phone number.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          return { isValid: false }
        }

        const phoneValidation = validatePhoneNumber(
          profile.phoneNumber,
          profile.phoneCountryCode
        )
        if (!phoneValidation.isValid) {
          toast({
            title: 'Validation Error',
            description:
              phoneValidation.errorMessage || 'Invalid phone number.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          return { isValid: false }
        }
      }

      return { isValid: true }
    },
    [toast]
  )

  return { validateProfile }
}
